import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Film, PlayCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Suggests 3 topical Christian videos inside a Lumera Companion response
 * card. Parent component resolves `topic` (slug) from the user question /
 * response payload; this component is purely the UI + fetch.
 *
 * Designed to be a no-op when `topic` is null or the topic has 0 videos,
 * so it gracefully collapses in offline / empty states.
 */
const RelatedVideosSection = ({ topic, language = 'en' }) => {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!topic) { setVideos([]); return; }
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/videos/topic/${topic}`, { params: { limit: 3 } })
      .then((r) => { if (!cancelled) setVideos(r.data?.videos || []); })
      .catch(() => { if (!cancelled) setVideos([]); });
    return () => { cancelled = true; };
  }, [topic]);

  if (!topic || videos.length === 0) return null;

  const t = {
    title: language === 'es' ? 'Videos relacionados' : 'Related videos',
    seeAll: language === 'es' ? 'Ver todos' : 'See all',
  };

  return (
    <>
      <Card className="shadow-md" data-testid="companion-related-videos">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Film className="w-4 h-4 text-primary" />
              {t.title}
            </h3>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7"
              onClick={() => navigate(`/videos`)}
              data-testid="companion-videos-see-all"
            >
              {t.seeAll}
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {videos.map((v) => (
              <button
                type="button"
                key={v.id}
                onClick={() => setSelected(v)}
                className="text-left group"
                data-testid={`companion-video-${v.id}`}
              >
                <div className="relative aspect-video bg-black/80 rounded-md overflow-hidden">
                  <img
                    src={v.thumbnail_url}
                    alt={v.title}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                    <PlayCircle className="w-8 h-8 text-white/90 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {v.duration_label && (
                    <span className="absolute bottom-1 right-1 px-1 py-0.5 bg-black/70 text-white text-[10px] rounded">
                      {v.duration_label}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-xs font-medium line-clamp-2">{v.title}</p>
                <p className="text-[10px] text-muted-foreground">{v.channel}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {selected && (
            <>
              <DialogHeader className="p-4 pb-2">
                <DialogTitle className="text-base pr-8">{selected.title}</DialogTitle>
                <p className="text-xs text-muted-foreground">{selected.channel}</p>
              </DialogHeader>
              <div className="relative aspect-video bg-black">
                <iframe
                  src={`${selected.embed_url}?autoplay=1&rel=0`}
                  title={selected.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Map free-text keywords (from a user question or an AI response) to a
 * canonical video-topic slug. Returns null when no strong match is found.
 */
export const inferVideoTopic = (text) => {
  if (!text || typeof text !== 'string') return null;
  const t = text.toLowerCase();
  const rules = [
    ['anxiety',     /\b(anxious|anxiety|worry|worried|stress|overwhelm)/],
    ['depression',  /\b(depress|hopeless|numb|despair|empty|can'?t get up)/],
    ['fear',        /\b(fear|afraid|scared|terrif|dread|nervous)/],
    ['grief',       /\b(grief|grieving|mourn|loss|death|died|funeral|bereav)/],
    ['hope',        /\b(hope|hopeful|future|longing)/],
    ['peace',       /\b(peace|calm|rest|shalom|still)/],
    ['forgiveness', /\b(forgiv|resent|bitter|unforg|grudge)/],
    ['faith',       /\b(faith|believe|belief|doubt|trust)/],
    ['prayer',      /\b(pray|prayer|intercess)/],
    ['purpose',     /\b(purpose|calling|meaning|mission|lost)/],
    ['marriage',    /\b(marriage|spouse|husband|wife|married|divorce)/],
    ['parenting',   /\b(parent|child|kids|son|daughter|mother|father|family)/],
  ];
  for (const [slug, re] of rules) {
    if (re.test(t)) return slug;
  }
  return null;
};

export default RelatedVideosSection;
